import ForwardToInboxRoundedIcon from "@mui/icons-material/ForwardToInboxRounded";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CompanyFilter from "../../../app/core/components/CompanyFilter/CompanyFilter";
import CoreAutoComplete from "../../../app/core/components/CoreAutoComplete/CoreAutoComplete";
import CoreButton from "../../../app/core/components/CoreButton/CoreButton";
import CoreDialog from "../../../app/core/components/CoreDialog/CoreDialog";
import CoreInputField from "../../../app/core/components/CoreInputField/CoreInputField";
import CoreSelectObjArr from "../../../app/core/components/CoreSelect/CoreSelectObjArr";
import { ROLE } from "../../../utils/constants/profiles";
import { TABLE_ROWS_NUMBER } from "../../../utils/constants/tables";
import {
  isCodiceFiscaleValid,
  isEmailValid,
} from "../../../utils/functions/form";
import { useGetCompaniesQuery } from "../../api/admin/adminCompany";
import { useGetCitiesQuery } from "../../api/admin/categoriesApi";
import { useGetProfileCategoryQuery } from "../../api/admin/gestioneNewsApi";
import { useGetStudioIdQuery } from "../../api/admin/gestisciStudioApi";
import {
  useDeleteUserMutation,
  useEditUserMutation,
} from "../../api/admin/gestisciUtentiApi";
import CoreSpinner from "../../../app/core/components/CoreSpinner/CoreSpinner";

const FormUtente = ({ userData, onClose, onActive, notesOperator }) => {
  const { t } = useTranslation();
  const appConfig = useSelector((state) => state.app.appConfig);

  const [formData, setFormData] = useState({});
  const [selectedCompany, setSelectedCompany] = useState({
    label: userData?.Data?.userCompany?.name || "",
    value: userData?.Data?.userCompany?.id,
  });
  const [debouncedInputCompanyValue, setDebouncedInputCompanyValue] =
    useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [showConfirmUserDialog, setShowConfirmUserDialog] = useState(false);
  const [isDichCongiunta, setIsDichCongiunta] = useState(
    userData.Data.isDichCongiunta ? true : false
  );

  const page = {
    currentPage: 1,
    startRow: 0,
    endRow: TABLE_ROWS_NUMBER,
  };

  const [editUser, { isLoading: isEditLoading }] = useEditUserMutation();
  const [deleteUser, { isLoading: isDeletingUser }] = useDeleteUserMutation();
  const { data: dataListCategory, isLoading: isLoadingListCategory } =
    useGetProfileCategoryQuery();

  const { data: studiosList, isLoading: isLoadingStudio } = useGetStudioIdQuery(
    formData.studioId,
    {
      skip: !formData.studioId,
    }
  );
  const {
    data: companiesList,
    isLoading: isCompanyLoading,
    isFetching: isCompanyFetching,
  } = useGetCompaniesQuery({ ...page, query: debouncedInputCompanyValue });

  const employmentOptions = dataListCategory?.Data.map((el) => ({
    label: el?.name,
    value: el?.id,
  }));

  const role = useSelector((state) => state.app.role);
  const colorPrimary = useSelector((state) => state.app.primaryColor);

  useEffect(() => {
    if (selectedCompany?.value)
      setFormData({
        ...formData,
        userCompanyId: selectedCompany?.value,
      });
    else
      setFormData({
        ...formData,
        userCompanyId: null,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany?.value, selectedCompany]);

  useEffect(() => {
    if (userData && userData.Data) {
      setFormData({
        name: userData.Data.name,
        surname: userData.Data.surname,
        fiscalCode: userData.Data.fiscalCode,
        common: {
          name: userData?.Data?.common?.name,
          cadastralCode: userData?.Data.common?.cadastralCode,
          province: userData?.Data?.common?.province,
        },
        address: userData.Data.address,
        email: userData.Data.email,
        phoneNumber: userData.Data.phoneNumber,
        adminId: userData.Data.adminId || null,
        employmentStatusId: userData.Data.employmentStatusId,
        studioId: userData.Data.studioId || null,
        userCompanyId: userData.Data.userCompanyId || null,
      });
    }
  }, [userData]);

  const handleOperatorLabel = (adminID) => {
    if (adminID) {
      const commercialistiOptions = studiosList?.Data?.admins?.map((el) => ({
        label: el?.name + " " + el?.surname,
        value: el?.id,
      }));
      return commercialistiOptions?.find((el) => el.value === adminID)?.label;
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleInputChangeAuto = async (event, newValue) => {
    setFormData((prevData) => ({
      ...prevData,
      common: {
        name: newValue?.name,
        cadastralCode: newValue?.cadastralCode,
        province: newValue?.province,
      },
    }));
  };

  const handleDelete = () => {
    setShowConfirmUserDialog(true);
  };

  const handleCloseDialog = () => {
    setShowConfirmUserDialog(false);
  };

  const handleConfirmDelete = () => {
    deleteUser(userData.Data.id)
      .then((res) => {
        if (res?.data?.Result !== 1) {
          toast.error(res.data.Exception);
          return;
        }
        toast.success(t("UserDeleted"));
        onClose();
      })
      .catch(() => {});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.name) {
      errors.name = t("FieldRequired");
    }
    if (!formData.surname) {
      errors.surname = t("FieldRequired");
    }
    if (!formData.email) {
      errors.email = t("FieldRequired");
    } else if (!isEmailValid(formData.email)) {
      errors.email = t("InsertValidMail");
    }
    if (formData.fiscalCode && !isCodiceFiscaleValid(formData.fiscalCode)) {
      errors.fiscalCode = t("InsertValidCF");
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    let body = {
      id: userData.Data.id,
      name: formData.name,
      surname: formData.surname,
      fiscalCode: formData.fiscalCode,
      common: {
        name: formData?.common?.name,
        cadastralCode: formData?.common?.cadastralCode,
        province: formData?.common?.province,
      },
      address: formData.address,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      adminId: formData.adminId || null,
      employmentStatusId: formData.employmentStatusId,
      studioId: formData.studioId || null,
      userCompanyId: formData.userCompanyId || null,
      isDichCongiunta: isDichCongiunta,
      operatorNotes: notesOperator,
    };

    editUser(body)
      .then((res) => {
        if (res.data && res.data.Exception) {
          toast.error(t("SavingNotOk"));
        } else {
          toast.success(t("UserUpdated"));
          onClose();
        }
      })
      .catch((error) => {});
  };

  const handleChangeRadio = (event) => {
    setIsDichCongiunta(event.target.value === "true");
  };

  if (isLoadingStudio || isCompanyLoading) {
    return <CoreSpinner />;
  }

  return (
    <div>
      <h3>{t("Data")}</h3>
      <div className="flex flex-row space-x-10 items-center ">
        <CoreInputField
          className="w-full"
          label={t("Name")}
          name="name"
          onChange={handleChange}
          value={formData?.name}
          error={Boolean(validationErrors?.name)}
          helperText={validationErrors?.name}
        />
        <CoreInputField
          className="w-full"
          label={t("Surname")}
          name="surname"
          onChange={handleChange}
          value={formData.surname}
          error={Boolean(validationErrors.surname)}
          helperText={validationErrors.surname}
        />
      </div>
      <div className="flex flex-row w-full space-x-10 items-center ">
        <div className="w-1/2">
          <CoreInputField
            className="w-full"
            label={t("CF")}
            name="fiscalCode"
            onChange={handleChange}
            value={formData.fiscalCode}
            error={Boolean(validationErrors.fiscalCode)}
            helperText={validationErrors.fiscalCode}
          />
        </div>
        <FormControl className="mt-10 w-1/2">
          <CoreSelectObjArr
            name="employmentStatusId"
            label={t("Role")}
            value={formData.employmentStatusId}
            items={employmentOptions}
            onChange={handleChange}
          />
        </FormControl>
      </div>
      <div className="flex mt-10 flex-col">
        <CoreAutoComplete
          name="common"
          common={formData?.common?.name}
          label={t("City")}
          onChange={handleInputChangeAuto}
          fetchData={useGetCitiesQuery}
        />

        <div className="flex space-x-10">
          <CoreInputField
            className="w-full"
            label={t("Address")}
            name="address"
            onChange={handleChange}
            value={formData.address}
          />
          <CoreInputField
            className="w-full"
            label={t("Phone")}
            name="phoneNumber"
            onChange={handleChange}
            value={formData.phoneNumber}
          />
        </div>
      </div>
      <div className="flex space-x-10">
        <CoreInputField
          disabled
          className="w-full"
          label="Email"
          name="email"
          onChange={handleChange}
          value={formData.email}
        />
      </div>

      {appConfig?.modules?.company?.general && (
        <div className="mt-6">
          <CompanyFilter
            selectedUser={selectedCompany}
            setSelectedUser={(v) => {
              setSelectedCompany(v);
              setValidationErrors((prev) => ({ ...prev, company: "" }));
            }}
            usersList={
              companiesList?.Data?.items.map((el) => ({
                label: el?.name,
                value: el?.id,
              })) || []
            }
            setDebouncedInputUserValue={setDebouncedInputCompanyValue}
            isUsersLoading={isCompanyLoading || isCompanyFetching}
            isError={validationErrors.company}
          />
        </div>
      )}

      {role === ROLE.ADMIN && (
        <div className="flex flex-col mt-6">
          <CoreInputField
            disabled
            className="w-full"
            label={t("Studio")}
            value={userData?.Data?.studioName}
          />
        </div>
      )}
      <div className="flex w-full mt-6 space-x-4 items-center">
        <CoreInputField
          disabled
          className="w-full"
          label={t("Operator")}
          value={handleOperatorLabel(formData.adminId)}
        />
      </div>

      <FormControl className="mt-6">
        <FormLabel>Dichiarazione congiunta?</FormLabel>

        <RadioGroup
          row
          value={isDichCongiunta}
          className="flex"
          onChange={handleChangeRadio}
        >
          <FormControlLabel
            value={true}
            control={<Radio size="small" />}
            label={t("Yes")}
          />
          <FormControlLabel
            value={false}
            control={<Radio size="small" />}
            label={t("No")}
          />
        </RadioGroup>
      </FormControl>

      <div className=" flex items-center mt-4">
        <h4 className="uppercase m-0" style={{ color: `${colorPrimary}` }}>
          {t("ResendEmail")}
        </h4>
        <IconButton className=" mx-4 " onClick={onActive}>
          <ForwardToInboxRoundedIcon />
        </IconButton>
      </div>

      <div className="flex justify-around mt-4 py-8">
        {role === ROLE.ADMIN && (
          <CoreButton
            testo={t("Delete")}
            className="bg-red-700 btn-round border-red-700"
            onClick={handleDelete}
            disabled={isDeletingUser}
          />
        )}
        <CoreButton
          disabled={isEditLoading || notesOperator?.length > 500}
          testo={isEditLoading ? t("Wait") : t("Save")}
          className="btn-round"
          onClick={handleSubmit}
        />
      </div>

      <CoreDialog
        open={showConfirmUserDialog}
        title={`${t("Delete")} ${t("user")}`}
        content={`${t("UserDeletingConfirm")} ${userData?.Data?.name} ${
          userData?.Data?.surname
        }?`}
        negativeButton={t("No")}
        positiveButton={t("Yes")}
        handlePositive={handleConfirmDelete}
        closeDialog={handleCloseDialog}
      />
    </div>
  );
};

export default FormUtente;
